// docs/51 friendly villages: the street and the rooms the village view paints, as pure seeded data.
// `villageLayout(look, cols)` lays the street out on a grid of 24 px cells, `cols` wide (the
// window's width in cells) and `STREET_H` tall: the buildings stand with their doors on row `BASE`,
// their roofs, chimneys and the temple's tower against the sky above it, the lane runs in front
// (rows 5–7) and a verge of hedges and fences closes the bottom row. The band stands where it
// came in, on the left (`ENTRY` cells); villagers stand on `spots` (never in a doorway). The same
// look and width always give the same street. `roomLayout` furnishes a building's inside the same way.
import { FILLS, GROUND, PROPS } from '../../../game/tiling/oryx';
import { STYLE_OF, type BuildingKind, type BuildingStyle, type VillageLook, type VillageSize, type VillageSpot, type WorkshopKind } from './types';

export const CELL = 24;
/** The street's height in cells: sky-side roofs (0–3), the doors (`BASE`), the lane, the verge and a
 * foreground of meadow (rows 9–10, so the people on the lane stand clear of a dialogue box at the bottom). */
export const STREET_H = 11;
/** The row every door stands on. */
export const BASE = 4;
export const LANE_ROWS = [5, 6, 7] as const;
export const VERGE = 8;
/** The cells on the left the band stands in (nothing is built there). */
export const ENTRY = 5;
/** One empty cell is kept at the right edge. */
const TAIL = 1;

export type Facade = BuildingKind | 'house';
export type WallKind = 'timber' | 'plaster' | 'stone' | 'tan' | 'log';
export type RoofKind = 'eaves' | 'gable' | 'flat';
export type RoofTone = 'tile' | 'slate' | 'thatch' | 'clay';
export type LaneKind = 'flags' | 'dirt' | 'sand';

export interface Building {
  kind: Facade; name: string;
  /** docs/112: what it is drawn like (a workshop takes the style of the building its trade is nearest) */ style: BuildingStyle | 'house';
  /** left cell and width in cells */ x: number; w: number;
  /** storeys (1: the door row only; 2: a row of windows above it) */ floors: 1 | 2;
  roof: RoofKind; roofTone: RoofTone; wall: WallKind;
  /** the door's cell */ door: number;
  /** a chimney's cell, if it has one */ chimney?: number;
  /** cells with a window on the door row / on the upper floor */ windows: number[]; upper: number[];
  /** 0–1, varies the plaster and the roof shade */ tone: number;
}
/** A building with a door the party may go through. */
export interface Door { kind: BuildingKind; name: string; x: number; w: number; door: number }
/** A world-atlas frame on a cell. `back` draws behind the buildings, `mid` with them, `front` over the people. */
export interface Prop { frame: number; x: number; y: number; anim?: readonly number[]; layer: 'back' | 'mid' | 'front'; flip?: boolean; /** pixel nudges */ dx?: number; dy?: number }
/** Where light falls at night: a lamp post, a lantern on a wall, a fire. `x`/`y` in cells (fractional). */
export interface Lamp { x: number; y: number; kind: 'post' | 'wall' | 'fire' | 'window' }
export interface Theme {
  biome: string; snowy: boolean; ground: number; groundAlt: number; lip: number;
  lane: LaneKind; walls: readonly WallKind[]; roofTone: RoofTone; flatRoofs: boolean;
  trees: readonly number[]; bushes: readonly number[]; verge: readonly number[];
  /** a hedge's three pieces (left, middle, right), where the country grows them */ hedges?: readonly [number, number, number];
  /** a fill for patches of long grass in the foreground */ meadow?: number;
}
export interface Square { x: number; w: number; kind: 'well' | 'board' | 'stall' }
export interface VillageLayout {
  w: number; h: number; theme: Theme;
  buildings: Building[]; doors: Door[];
  /** x cells villagers may stand on (rows 5–6), none in a doorway or the band's corner */
  spots: number[];
  /** where the band's heroes stand (cell x, on row 6) */
  heroes: number[];
  /** ground fills, row by row (`ground[y][x]` = frames, bottom first) */
  ground: number[][][];
  props: Prop[]; lamps: Lamp[]; square: Square;
  /** two cells a villager may stroll between */ stroll: [number, number];
  /** verge cells with a wooden fence (row `VERGE`) */ fences: number[];
  /** docs/121 VW7: the cells of countryside either side of the village (`withOutskirts`; the village starts at this x) */ margin?: number;
}

// ---- seeded randomness --------------------------------------------------------------------------
export function rng(seed: number): () => number {
  let a = (seed | 0) ^ 0x9e3779b9;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
export const hashN = (...n: number[]): number => {
  let h = 0x811c9dc5;
  for (const v of n) {
    h = Math.imul(h ^ (Math.round(v * 977) | 0), 0x01000193);
    h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  }
  return (h >>> 0) / 4294967296;
};
const pickOf = <T,>(r: () => number, list: readonly T[]): T => list[Math.floor(r() * list.length) % list.length]!;

// ---- the country's look -------------------------------------------------------------------------
const T = PROPS.trees;
const HEDGE_GREEN = [PROPS.bushDense[0], PROPS.bushDense[1], PROPS.bushDense[0]] as const;
export function themeFor(look: Pick<VillageLook, 'biome' | 'season'>): Theme {
  const biome = (look.biome ?? 'temperate').toLowerCase();
  const season = look.season ?? 'summer';
  const winter = season === 'winter' && biome !== 'desert' && biome !== 'jungle' && biome !== 'underdark' && biome !== 'ashlands';
  const autumn = season === 'autumn';
  const g = (k: keyof typeof GROUND) => GROUND[k]!.base;
  const snowGround = { ground: g('snow'), groundAlt: g('snow') + 1, lip: g('snow') };
  switch (biome) {
    case 'desert':
      return { biome, snowy: false, ground: g('peach'), groundAlt: g('peach2'), lip: g('peach'), lane: 'sand', walls: ['tan'], roofTone: 'clay', flatRoofs: true,
        trees: [PROPS.cacti[0], PROPS.cacti[1], PROPS.cacti[2], T.dead], bushes: [PROPS.roundBushBrown[0], PROPS.roundBushBrown[1], PROPS.rocksOrange[0]], verge: [PROPS.roundBushBrown[1], PROPS.tallGrassOrange[0], PROPS.cacti[2], PROPS.tallGrassOrange[2]] };
    case 'boreal':
      return { biome, snowy: winter, ...(winter ? snowGround : { ground: g('darkGreen'), groundAlt: g('darkGreen') + 1, lip: g('darkGreen') }), lane: 'dirt', walls: ['log', 'log', 'timber'], roofTone: 'slate', flatRoofs: false,
        trees: winter ? [T.snowPineSingle[0], T.snowPineSingle[1], T.snowPineSingle[2], T.snowPine] : [T.pineSingle[0], T.pineSingle[1], T.pineSingle[2], T.pine],
        bushes: [PROPS.bushSmall[2], PROPS.roundBush[1]], verge: [PROPS.shrubs.tufts[0], PROPS.bushSmall[2], PROPS.shrubs.tufts[2]], ...(winter ? {} : { hedges: HEDGE_GREEN }) };
    case 'highland':
      return { biome, snowy: winter, ...(winter ? snowGround : { ground: g('tan'), groundAlt: g('tan') + 1, lip: g('tan') }), lane: 'flags', walls: ['stone', 'stone', 'plaster'], roofTone: 'slate', flatRoofs: false,
        trees: winter ? [T.snowPineSingle[0], T.snowPineSingle[2], T.snowPine] : [T.pineSingle[0], T.pineSingle[2], T.pine], bushes: [PROPS.rocksWhite[0], PROPS.boulder, PROPS.shrubs.tufts[1]], verge: [PROPS.shrubs.tufts[0], PROPS.shrubs.tufts[2], PROPS.shrubs.tufts[1]] };
    case 'jungle':
      return { biome, snowy: false, ground: g('darkGreen'), groundAlt: g('darkGreen') + 1, lip: g('darkGreen'), lane: 'dirt', walls: ['timber', 'log'], roofTone: 'thatch', flatRoofs: false,
        trees: [T.fruit, T.fruit, T.oak, PROPS.bushDense[0]], bushes: [PROPS.starPlant[0], PROPS.starPlant[1], PROPS.bushDense[0]], verge: [PROPS.starPlant[1], PROPS.flowers[2], PROPS.bushSmall[0]], hedges: HEDGE_GREEN };
    // docs/23 Y9: a cave town of stone and slate among the glowing fungus, dark rock and crystal
    case 'underdark':
      return { biome, snowy: false, ground: g('tan'), groundAlt: g('tan') + 1, lip: g('tan'), lane: 'flags', walls: ['stone', 'stone', 'plaster'], roofTone: 'slate', flatRoofs: true,
        trees: [PROPS.starPlant[0], PROPS.starPlant[1], PROPS.rocksDarkGrey[0], PROPS.crystals.grey[0]], bushes: [PROPS.shrubs.teal[0], PROPS.shrubs.teal[2], PROPS.boulder], verge: [PROPS.shrubs.teal[1], PROPS.rocks, PROPS.shrubs.teal[0]] };
    // docs/23 Y10: a town of black basalt and slate among dead trees, dark crags and glowing vents
    case 'ashlands':
      return { biome, snowy: false, ground: g('tan'), groundAlt: g('tan') + 1, lip: g('tan'), lane: 'flags', walls: ['stone', 'stone', 'stone'], roofTone: 'slate', flatRoofs: true,
        trees: [T.dead, T.dead, PROPS.rocksDarkGrey[0], PROPS.rocksRed[0]], bushes: [PROPS.roundBushBrown[0], PROPS.boulder, PROPS.crystals.orange], verge: [PROPS.rocks, PROPS.rocksDarkGrey[3], PROPS.roundBushBrown[1]] };
    case 'wetland':
      return { biome, snowy: winter, ...(winter ? snowGround : { ground: g('olive'), groundAlt: g('olive') + 1, lip: g('olive') }), lane: 'dirt', walls: ['timber', 'log', 'plaster'], roofTone: 'thatch', flatRoofs: false,
        trees: winter ? [T.dead, T.snowPineSingle[1]] : autumn ? [T.autumnSingle[0], T.dead, T.autumnSingle[2]] : [T.oakSingle[0], T.dead, T.oakSingle[2]],
        bushes: [PROPS.tallGrassOrange[0], PROPS.starPlant[0], PROPS.bushSmall[2]], verge: [PROPS.tallGrassOrange[1], PROPS.tallGrassOrange[2], PROPS.starPlant[0]] };
    default:
      return { biome: 'temperate', snowy: winter, ...(winter ? snowGround : { ground: g('grass'), groundAlt: g('grass') + 1, lip: g('grass') }),
        lane: 'flags', walls: ['timber', 'plaster', 'timber'], roofTone: 'tile', flatRoofs: false,
        trees: winter ? [T.snowPineSingle[0], T.snowPineSingle[2], T.snowPineSingle2[1], T.snowPine] : autumn ? [T.autumnSingle[0], T.oakSingle[1], T.autumnSingle[2], T.oakSingle[0], T.autumnSingle[1]] : [T.oakSingle[0], T.oakSingle[1], T.oakSingle[2], T.oak, T.fruit],
        bushes: autumn ? [PROPS.bushSmallOrange[0], PROPS.shrubs.orange[1]] : [PROPS.roundBush[0], PROPS.bushSmall[0], PROPS.shrubs.green[1]],
        verge: winter ? [PROPS.shrubs.tufts[0], PROPS.shrubs.tufts[2]] : autumn ? [PROPS.leaves[2], PROPS.leaves[3], PROPS.shrubs.orange[0]] : season === 'spring' ? [PROPS.flowers[0], PROPS.flowers[1], PROPS.flowers[2]] : [PROPS.flowers[1], PROPS.shrubs.green[0], PROPS.flowers[2]],
        ...(winter ? {} : { hedges: HEDGE_GREEN }) };
  }
}

// ---- the street ---------------------------------------------------------------------------------
const WIDTH = (k: Facade): number => (k === 'inn' ? 4 : 3);
const styleOf = (k: Facade): BuildingStyle | 'house' => (k === 'house' ? 'house' : STYLE_OF[k]);
const SQUARE_W = 3;
/** The narrowest street a size lays out on (the band's corner, its buildings, the square, a gap each). */
export function minCols(size: VillageSize, buildings = size === 'hamlet' ? 2 : size === 'village' ? 3 : 4): number {
  // the inn is 4 wide, every other building 3
  return ENTRY + 4 + 3 * Math.max(0, buildings - 1) + SQUARE_W + buildings + TAIL;
}

type Slot = { t: 'b'; kind: Facade; name: string; w: number } | { t: 'square'; w: number } | { t: 'gap'; w: number };

export function villageLayout(look: VillageLook, cols?: number, margin = 0): VillageLayout {
  const inner = streetOf(look, cols);
  return margin > 0 ? withOutskirts(inner, look, margin) : inner;
}

/** docs/121 VW7: the cells of countryside drawn either side of the village's street, to pan and zoom out over. */
export const OUTSKIRTS = 10;

/**
 * docs/121 VW7: the street with `m` cells of countryside either side: the lane runs on out of the village
 * as a road between fields, trees and hedges. The village itself is the same street as without them,
 * moved `m` cells right.
 */
export function withOutskirts(l: VillageLayout, look: VillageLook, m: number): VillageLayout {
  const r = rng(look.seed * 13 + 5);
  const w = l.w + 2 * m;
  const theme = l.theme;
  const sx = (x: number) => x + m;
  const buildings = l.buildings.map((b) => ({ ...b, x: sx(b.x), door: sx(b.door), windows: b.windows.map(sx), upper: b.upper.map(sx), ...(b.chimney !== undefined ? { chimney: sx(b.chimney) } : {}) }));
  const doors = l.doors.map((d) => ({ ...d, x: sx(d.x), door: sx(d.door) }));
  const props: Prop[] = l.props.map((p) => ({ ...p, x: sx(p.x) }));
  const lamps = l.lamps.map((q) => ({ ...q, x: sx(q.x) }));
  const fences = l.fences.map(sx);
  const out = (cx: number) => cx < m || cx >= m + l.w;
  // the road on out between the fields: the lane's fill, grass lips along both edges
  const grass = (cx: number, cy: number) => (hashN(look.seed, cx, cy, 3) < 0.72 ? theme.ground : theme.groundAlt);
  const ground: number[][][] = l.ground.map((row, cy) => {
    const cells = (from: number, to: number) => Array.from({ length: to - from }, (_, i) => {
      const cx = from + i;
      if (cy < BASE) return [] as number[];
      if (cy === BASE || cy >= VERGE) return [cy > VERGE && theme.meadow && hashN(look.seed, cx, cy, 23) < 0.3 ? theme.meadow : grass(cx, cy)];
      const lane = theme.lane === 'sand' ? FILLS.sand : FILLS.dirt;
      return cy === 5 ? [lane, theme.lip + 19] : cy === 7 ? [lane, theme.lip + 21] : [lane];
    });
    return [...cells(0, m), ...row, ...cells(m + l.w, w)];
  });
  const taken = new Set(props.map((p) => `${p.x},${p.y}`));
  const put = (p: Prop) => { if (taken.has(`${p.x},${p.y}`)) return; props.push(p); taken.add(`${p.x},${p.y}`); };
  const hedge: number[] = [];
  for (let cx = 0; cx < w; cx++) {
    if (!out(cx)) continue;
    // the wood behind comes down to the fields: trees back and front of the door row, bushes between
    if (hashN(look.seed, cx, 2) < 0.75) put({ frame: pickOf(r, theme.trees), x: cx, y: 3, layer: 'back', dy: -Math.floor(hashN(look.seed, cx, 7) * 8) });
    const v = hashN(look.seed, cx, 31);
    if (v < 0.5) put({ frame: pickOf(r, theme.trees), x: cx, y: BASE, layer: 'mid' });
    else if (v < 0.8) put({ frame: pickOf(r, theme.bushes), x: cx, y: BASE, layer: 'mid' });
    // the verge: hedges or a fence, flowers in the meadow, a tree now and then in front
    const u = hashN(look.seed, cx, 37);
    if (theme.hedges && u < 0.45) hedge.push(cx);
    else if (u < 0.75 && theme.biome !== 'desert') fences.push(cx);
    else if (u < 0.9) put({ frame: pickOf(r, theme.verge), x: cx, y: VERGE, layer: 'front' });
    const f = hashN(look.seed, cx, 41);
    if (f < 0.2) put({ frame: pickOf(r, theme.verge), x: cx, y: VERGE + 1 + (f < 0.08 ? 1 : 0), layer: 'front' });
    else if (f < 0.32) put({ frame: pickOf(r, theme.trees), x: cx, y: VERGE + 2, layer: 'front', dy: -4 });
  }
  // a hedge's runs: rounded off at each end
  const hs = new Set(hedge);
  if (theme.hedges) for (const cx of hedge) put({ frame: theme.hedges[!hs.has(cx - 1) ? 0 : !hs.has(cx + 1) ? 2 : 1], x: cx, y: VERGE, layer: 'front' });
  return {
    ...l, w, buildings, doors, props, lamps, fences: [...new Set(fences)].sort((a, b) => a - b), ground,
    spots: l.spots.map(sx), square: { ...l.square, x: sx(l.square.x) }, stroll: [sx(l.stroll[0]), sx(l.stroll[1])], margin: m,
  };
}

function streetOf(look: VillageLook, cols?: number): VillageLayout {
  const r = rng(look.seed * 7 + 1);
  const theme = themeFor(look);
  const size = look.size;
  const real = look.buildings.length ? look.buildings : [{ kind: 'inn' as const, name: 'The Inn' }];
  const need = minCols(size, real.length);
  const w = Math.max(need, Math.floor(cols ?? need));
  const h = STREET_H;

  // the order along the street: each building, the square after the first, a gap after each
  const slots: Slot[] = [];
  real.forEach((b, i) => {
    slots.push({ t: 'b', kind: b.kind, name: b.name, w: WIDTH(b.kind) });
    if (i === 0) slots.push({ t: 'square', w: SQUARE_W });
    slots.push({ t: 'gap', w: 1 });
  });
  const used = () => ENTRY + TAIL + slots.reduce((s, x) => s + x.w, 0);
  // fill the rest of the window with houses (and a wider gap now and then), seeded
  const wantHouses = size === 'hamlet' ? 1 : size === 'village' ? 2 : 3;
  let houses = 0;
  for (let guard = 0; guard < 40 && used() < w; guard++) {
    const spare = w - used();
    if (spare >= 2 && (houses < wantHouses || r() < 0.8)) {
      const hw = spare >= 4 ? (r() < 0.5 ? 2 : 3) : Math.min(spare - (spare > 2 ? 1 : 0), 3);
      if (hw < 2) { slots.push({ t: 'gap', w: spare }); break; }
      // houses go anywhere after the square; terraces (no gap) happen
      const at = 2 + Math.floor(r() * (slots.length - 1));
      const terrace = r() < 0.35 || spare < hw + 1;
      slots.splice(at, 0, { t: 'b', kind: 'house', name: '', w: hw }, ...(terrace ? [] : [{ t: 'gap' as const, w: 1 }]));
      houses++;
    } else {
      const gaps = slots.filter((s) => s.t === 'gap');
      pickOf(r, gaps.length ? gaps : [slots[slots.length - 1]!]).w += 1;
    }
  }

  const buildings: Building[] = [];
  const doors: Door[] = [];
  let square: Square = { x: ENTRY, w: SQUARE_W, kind: 'well' };
  let x = ENTRY;
  const gapCells: number[] = [];
  for (const s of slots) {
    if (s.t === 'gap') { for (let i = 0; i < s.w; i++) gapCells.push(x + i); x += s.w; continue; }
    if (s.t === 'square') { square = { x, w: s.w, kind: look.haven ? 'board' : size === 'town' ? 'stall' : 'well' }; x += s.w; continue; }
    const b = makeBuilding(s.kind, s.name, x, s.w, theme, r);
    buildings.push(b);
    if (s.kind !== 'house') doors.push({ kind: s.kind, name: s.name, x, w: s.w, door: b.door });
    x += s.w;
  }
  for (let i = x; i < w; i++) gapCells.push(i);
  for (let i = 0; i < ENTRY; i++) gapCells.push(i);

  // ── the ground: grass behind and at the front, the lane between ──
  const ground: number[][][] = Array.from({ length: h }, () => Array.from({ length: w }, () => [] as number[]));
  const builtAt = (cx: number) => buildings.some((b) => cx >= b.x && cx < b.x + b.w);
  const lane = (cx: number, cy: number): number => {
    const v = hashN(look.seed, cx, cy, 11);
    switch (theme.lane) {
      case 'flags': return FILLS.dirt;          // a flagged walk before a town's doors
      case 'sand': return v < 0.7 ? FILLS.sand : GROUND.peach2!.base;
      default: return FILLS.dirt;
    }
  };
  const grass = (cx: number, cy: number) => (hashN(look.seed, cx, cy, 3) < 0.72 ? theme.ground : theme.groundAlt);
  for (let cy = BASE; cy < h; cy++) for (let cx = 0; cx < w; cx++) {
    const cell = ground[cy]![cx]!;
    if (cy === BASE) { cell.push(builtAt(cx) ? lane(cx, cy) : grass(cx, cy)); continue; }
    if (cy >= VERGE) { cell.push(cy > VERGE && theme.meadow && hashN(look.seed, cx, cy, 23) < 0.3 ? theme.meadow : grass(cx, cy)); continue; }
    cell.push(lane(cx, cy));
    // grass tongues over the lane's edges (the ground set's outward lips)
    if (cy === 5 && !builtAt(cx)) cell.push(theme.lip + 19);
    if (cy === 7) cell.push(theme.lip + 21);
  }

  // ── props ──
  const props: Prop[] = [];
  const lamps: Lamp[] = [];
  const taken = new Set<string>();
  const put = (p: Prop) => { props.push(p); taken.add(`${p.x},${p.y}`); };
  // the wood behind the village: trees standing back of the roofs (seen over the low houses and in the gaps)
  for (let cx = 0; cx < w; cx++) {
    if (hashN(look.seed, cx, 2) < 0.5) put({ frame: pickOf(r, theme.trees), x: cx, y: 3, layer: 'back', dy: -Math.floor(hashN(look.seed, cx, 7) * 8) });
  }
  // in the gaps: a tree or a bush on the door row, a fence now and then
  for (const cx of gapCells) {
    const v = hashN(look.seed, cx, 13);
    if (cx < ENTRY && cx > 1) continue;   // the band's corner stays open towards the road
    if (v < 0.55) put({ frame: pickOf(r, theme.trees), x: cx, y: BASE, layer: 'mid' });
    else if (v < 0.85) put({ frame: pickOf(r, theme.bushes), x: cx, y: BASE, layer: 'mid' });
  }
  // the square
  const sq = square;
  if (sq.kind === 'well') put({ frame: PROPS.well, x: sq.x + 1, y: 5, layer: 'mid' });
  if (sq.kind === 'stall') { put({ frame: PROPS.crate[0], x: sq.x, y: BASE, layer: 'mid' }); put({ frame: PROPS.well, x: sq.x + 2, y: 5, layer: 'mid' }); }
  if (sq.kind === 'board') put({ frame: PROPS.barrel[0], x: sq.x + 2, y: BASE, layer: 'mid' });
  for (const b of buildings) {
    // barrels, crates and buckets by the doors (never in front of a window's cell if avoidable)
    const free = Array.from({ length: b.w }, (_, i) => b.x + i).filter((cx) => cx !== b.door);
    const dress: Partial<Record<Facade, readonly number[]>> = {
      inn: [PROPS.barrel[0], PROPS.barrel[1]], shop: [PROPS.crate[0], PROPS.urns.red[0], PROPS.crate[1]], smithy: [PROPS.furniture.tableCandles[0], PROPS.bucket[0]],
      temple: [PROPS.urns.grey[1]], house: [PROPS.barrel[1], PROPS.bucket[1], PROPS.crate[1], PROPS.flowers[1]],
    };
    const list = dress[b.style] ?? [];
    const n = b.style === 'house' ? (r() < 0.6 ? 1 : 0) : b.style === 'temple' ? 0 : Math.min(list.length, free.length, b.style === 'inn' ? 2 : 1 + Math.floor(r() * 2));
    for (let i = 0; i < n; i++) {
      const cx = free.splice(Math.floor(r() * free.length), 1)[0]!;
      put({ frame: b.style === 'house' ? pickOf(r, list) : list[i % list.length]!, x: cx, y: BASE, layer: 'mid', dy: 1 });
    }
    // lanterns on the walls by the doors that matter, lamp posts on the lane in a village or town
    if (b.kind !== 'house') lamps.push({ x: b.door + (b.door > b.x ? -0.15 : 1.15), y: BASE - 0.15, kind: 'wall' });
    if (b.style === 'smithy') lamps.push({ x: b.door + 0.5, y: BASE + 0.4, kind: 'fire' });
  }
  if (size !== 'hamlet') {
    for (let cx = ENTRY + 2 + Math.floor(r() * 3); cx < w - 1; cx += 7 + Math.floor(r() * 3)) {
      if (!doors.some((d) => d.door === cx)) lamps.push({ x: cx + 0.5, y: 5.2, kind: 'post' });
    }
  }
  // the verge: hedge runs, a wooden fence (painted by the view), flowers; then the meadow in front
  const fences: number[] = [];
  for (let cx = 0; cx < w;) {
    const v = hashN(look.seed, cx, 17);
    const run = 2 + Math.floor(hashN(look.seed, cx, 18) * 3);
    if (v < 0.38 && theme.hedges) {
      for (let i = 0; i < run && cx + i < w; i++) put({ frame: theme.hedges[i === 0 ? 0 : i === run - 1 || cx + i === w - 1 ? 2 : 1], x: cx + i, y: VERGE, layer: 'front' });
      cx += run + 1;
    } else if (v < 0.7 && theme.biome !== 'desert') {
      for (let i = 0; i < run && cx + i < w; i++) fences.push(cx + i);
      cx += run;
    } else {
      if (theme.biome === 'desert' ? hashN(look.seed, cx, 29) < 0.3 : v < 0.88) put({ frame: pickOf(r, theme.verge), x: cx, y: VERGE, layer: 'front' });
      cx += 1;
    }
  }
  // a tree or two right in front, framing the view (the view shades them)
  for (const fx of [0, w - 1, ...(r() < 0.6 ? [Math.floor(w * (0.3 + r() * 0.4))] : [])]) put({ frame: pickOf(r, theme.trees), x: fx, y: VERGE + 2, layer: 'front', dy: -4 });
  for (let cx = 0; cx < w; cx++) {
    const u = hashN(look.seed, cx, 19);
    if (u < 0.22) put({ frame: pickOf(r, theme.verge), x: cx, y: VERGE + 1 + (u < 0.1 ? 1 : 0), layer: 'front' });
    else if (u < 0.3) put({ frame: pickOf(r, theme.bushes), x: cx, y: VERGE + 2, layer: 'front' });
  }

  // ── where people stand ──
  const blocked = new Set(props.filter((p) => p.layer === 'mid' && p.y >= 5).map((p) => p.x));
  const doorCells = new Set(doors.map((d) => d.door).concat(buildings.map((b) => b.door)));
  const spots: number[] = [];
  for (let cx = ENTRY; cx < w - TAIL; cx++) if (!doorCells.has(cx) && !blocked.has(cx)) spots.push(cx);
  const heroes = [1, 2, 3, 4];
  const open = spots.filter((s) => s > ENTRY + 1);
  const a = open[Math.floor(r() * Math.max(1, open.length - 4))] ?? ENTRY + 1;
  const stroll: [number, number] = [a, Math.min(w - TAIL - 1, a + 4 + Math.floor(r() * 3))];
  return { w, h, theme, buildings, doors, spots, heroes, ground, props, lamps, square, stroll, fences };
}

function makeBuilding(real: Facade, name: string, x: number, w: number, theme: Theme, r: () => number): Building {
  // docs/112: a workshop is built like the building its trade is nearest
  const kind = styleOf(real);
  const tone = r();
  const wallOf = (): WallKind => {
    if (kind === 'smithy' || kind === 'temple') return theme.biome === 'desert' ? 'tan' : 'stone';
    return pickOf(r, theme.walls);
  };
  const wall = wallOf();
  const floors: 1 | 2 = kind === 'inn' || kind === 'temple' ? 2 : kind === 'smithy' ? 1 : kind === 'shop' ? (r() < 0.5 ? 2 : 1) : r() < 0.55 ? 2 : 1;
  const roof: RoofKind = theme.flatRoofs ? 'flat' : kind === 'temple' ? 'gable' : kind === 'house' ? (r() < 0.5 ? 'gable' : 'eaves') : kind === 'smithy' ? 'eaves' : r() < 0.4 ? 'gable' : 'eaves';
  const door = kind === 'inn' ? x + 1 + Math.floor(r() * 2) : kind === 'temple' || w === 3 ? x + 1 : x + Math.floor(r() * w);
  const cells = Array.from({ length: w }, (_, i) => x + i);
  const windows = kind === 'smithy' ? [] : cells.filter((c) => c !== door && (kind !== 'temple'));
  const upper = floors === 2 ? cells.filter((c) => kind !== 'temple' || c === door) : [];
  const chimney = kind === 'temple' ? undefined : kind === 'smithy' ? x + w - 1 : r() < 0.8 ? (r() < 0.5 ? x : x + w - 1) : undefined;
  // docs/112: the towers of the enchanter and the stargazer are roofed in slate and the witch's
  // cottage in thatch, so none is taken for the temple or the inn
  const roofTone: RoofTone = real === 'enchanter' || real === 'stargazer' ? (theme.roofTone === 'slate' ? 'clay' : 'slate')
    : real === 'hedgewitch' ? (theme.roofTone === 'thatch' ? 'slate' : 'thatch')
    : kind === 'temple' && theme.roofTone === 'thatch' ? 'slate' : theme.roofTone;
  return { kind: real, style: kind, name, x, w, floors, roof, roofTone, wall, door, ...(chimney !== undefined ? { chimney } : {}), windows, upper, tone };
}

// ---- inside ------------------------------------------------------------------------------------
/** Where a room's people stand: the keeper at their post, others at tables or along the floor. */
export interface Station { x: number; y: number; /** sitting (only the head and shoulders show over the table) */ seated?: boolean; flip?: boolean }
/** An item-atlas icon (16 px) hung on a wall or set on a shelf; x/y in cells. */
export interface WallItem { item: number; x: number; y: number }
export interface Room {
  kind: BuildingKind; w: number; h: number;
  /** the floor's back row (the back wall is above it) */ floor: number;
  /** the wall's top row (rafters above it) */ top: number;
  props: Prop[]; lamps: Lamp[];
  keeper: Station; others: Station[];
  /** the way out (bottom left) */ exit: number;
  /** windows in the back wall (cells; `h` rows tall) */ windows: Array<{ x: number; y: number; h: number }>;
  /** a hearth or forge built into the wall: its left cell, 3 wide, standing on the floor */ hearth?: { x: number; y: number };
  /** painted wall shelves (cells, `w` wide) and what stands or hangs on them */ shelves: Array<{ x: number; y: number; w: number }>; items: WallItem[];
  /** lights hung from the rafters (cell x) */ hangs: Array<{ x: number; kind: 'lantern' | 'chandelier' }>;
  /** pews (temple) and a rug on the floor */ pews: Array<{ x: number; y: number; w: number }>; rug?: { x: number; y: number; w: number; h: number; blue: boolean };
  /** where the band stands, just in from the door */ band: Station;
  /** docs/64 VP4: the inn's long table, where a band sits down to a meal (cells; its top row is `y`) */ table?: { x: number; y: number; w: number };
  /** docs/73 TP4: every table a diner can sit at, the long one first (`seats.ts` lays their places) */ tables?: Array<{ x: number; y: number; w: number }>;
  /** docs/116: more rugs (the hearth rug at the inn) */ rugs?: Array<{ x: number; y: number; w: number; h: number; blue: boolean }>;
  /** docs/116: furniture the inn's own layer paints that people cannot walk through (stations' cells) */ blocks?: Array<{ x: number; y: number; front?: boolean }>;
  /** docs/116: where the inn's things stand (the inn and the room upstairs only) */ inn?: InnPlan;
  /** docs/142: the dyer's yard: open vats of dye on the floor (`dye` a `DYES` id) and lines of dyed cloth hung across the back wall */
  dyer?: { vats: Array<{ x: number; y: number; dye: number }>; lines: Array<{ x: number; y: number; w: number; from: number }> };
}

/** docs/116 the cozy inn: where each of the inn's things stands, in cells (stations are a figure's top-left). */
export interface InnPlan {
  /** The hearth's front: where the band stands to warm its hands, facing the fire. */
  hearth: Station[];
  /** CI8: two armchairs either side of the fire, each facing it (`flip` faces left), and their side tables. */
  armchairs: Station[];
  /** The fire's middle (cells), for sparks when it is poked. */
  fire: { x: number; y: number };
  /** CI9: the storyteller's stool by the hearth. */
  storyteller: Station;
  /** CI13: the hearth rug the cat sleeps on, the dog's spot by the door. */
  cat: Station; dog: Station;
  /** CI10: the noticeboard on the back wall (cells, w × h) and CI11 the guestbook's lectern on the floor. */
  notice: { x: number; y: number; w: number; h: number };
  lectern: Station;
  /** CI7: the bard's corner. */
  bard: Station;
  /** CI12: the bar: the counter's cells, where the barkeep pours (a keg) and where a drinker stands. */
  bar: { x: number; w: number; y: number; tap: { x: number; y: number }; stand: Station[] };
  /** CI14: the kitchen door in the back wall (a serving hand comes out here) and its bell. */
  kitchen: Station; bell: { x: number; y: number };
  /** CI4/CI5: the dice table (its top, cells) and its four stools, left to right. */
  dice: { x: number; y: number; w: number; stools: Station[] };
  /** CI16: the stairs up (bottom step's cell and the top), or (upstairs) the way down. */
  stairs: { x: number; y: number; top: { x: number; y: number } };
  /** CI14: a candle on each table (cells, where its foot stands) and the tables' index in `tables`. */
  candles: Array<{ x: number; y: number; table: number }>;
  /** CI14: the windows a hero gazes out of: where they stand at each (`windows` order). */
  gaze: Station[];
  /** CI16 (upstairs): the beds (left cell, 2 wide, the pillow on the left) and where a sleeper lies. */
  beds: Array<{ x: number; y: number; sleeper: Station }>;
  /** CI8: the little table by the armchairs (a candle and a book on it; its foot's cell). */
  side?: { x: number; y: number };
  /** CI2: a gallery across the upper back wall where the rafters are tall enough (rows ≥ 3 above the
   * wall): its floor's top (cells), its span, and the doors to the rooms along it (cell x). */
  gallery?: { y: number; x0: number; x1: number; doors: number[] };
  /** CI16 (upstairs): the wash stand (its cell). */
  wash?: { x: number; y: number };
}

/** Floor rows at the bottom of a room: furniture stands at the back, the people in front of it stay
 * clear of a dialogue box over the bottom of the window. */
export const ROOM_FLOOR = 5;
/** A room's furnishing spans this many cells about its middle; wider windows show more wall and floor. */
export const ROOM_SPAN = 18;
/** docs/116 CI2: the inn is furnished across more of the room (its scale is chosen to fit this). */
export const INN_SPAN = 26;
/** The span a room's furnishing needs. */
export const spanOf = (kind: BuildingKind): number => (kind === 'inn' ? INN_SPAN : ROOM_SPAN);

/** docs/120 VM5: every player sees one street and one room the same size, whatever their window, so a
 * place in cells is the same place in every view (the camera scales the stage to the window). The
 * street is at least `STREET_COLS` wide; every room is `ROOM_COLS` × `ROOM_ROWS`, its floor `ROOM_FLOOR_ROWS` deep. */
export const STREET_COLS = 27;
export const ROOM_COLS = 30;
export const ROOM_ROWS = 18;
export const ROOM_FLOOR_ROWS = 7;
/** The street's width in cells for a village (`villageLayout`'s `cols`). */
export const streetCols = (size: VillageSize, buildings: number): number => Math.max(minCols(size, buildings), STREET_COLS);

/**
 * The whole-number scale (2 or 3) to paint a stage of `cols` × `rows` cells at in a window: the one the
 * camera has to stretch or shrink least to cover the window's width (the street, which stands on the
 * window's foot with sky above, and is only ever scaled up) or all of it (a room). A street at 3 needs
 * the window tall enough for its rows and a dialogue box.
 */
export function stageScale(cols: number, rows: number, view: { w: number; h: number }, room: boolean): 2 | 3 {
  const cost = (s: number) => {
    const z = room ? Math.max(view.w / (cols * CELL * s), view.h / (rows * CELL * s)) : Math.max(1, view.w / (cols * CELL * s));
    // a street wider than the window is panned, which costs as much as shrinking it would
    const over = room ? 1 : Math.min(1, view.w / (cols * CELL * s));
    return Math.abs(Math.log(z)) + Math.abs(Math.log(over));
  };
  if (!room && view.h < STREET_H * CELL * 3 + 160) return 2;
  return cost(3) < cost(2) ? 3 : 2;
}

/** `cols` × `rows` cells: rafters, the back wall, then the floor (the last `ROOM_FLOOR` rows). */
export function roomLayout(kind: BuildingKind, seed: number, cols: number, rows: number, floorRows = ROOM_FLOOR): Room {
  const r = rng(seed * 13 + kind.length * 101);
  const w = Math.max(spanOf(kind) + 1, cols), h = Math.max(floorRows + 6, rows);
  const F = h - floorRows;          // the floor's back row: what stands against the wall
  const top = F - 5;                 // the wall's top: rafters above
  const cx = Math.floor(w / 2);
  const props: Prop[] = [];
  const lamps: Lamp[] = [];
  const others: Station[] = [];
  const windows: Room['windows'] = [];
  const shelves: Room['shelves'] = [];
  const items: WallItem[] = [];
  const hangs: Room['hangs'] = [];
  const pews: Room['pews'] = [];
  let rug: Room['rug'];
  let table: Room['table'];
  let tables: Room['tables'];
  const rugs: NonNullable<Room['rugs']> = [];
  const blocks: NonNullable<Room['blocks']> = [];
  let inn: InnPlan | undefined;
  const P = PROPS;
  let keeper: Station = { x: cx, y: F + 1 };
  let hearth: Room['hearth'];
  const put = (frame: number, x: number, y: number, layer: Prop['layer'] = 'mid', extra: Partial<Prop> = {}) => props.push({ frame, x, y, layer, ...extra });
  const banner = (x: number, y: number, blue: boolean) => {
    const [tl, tr, ml, mr, bl, br] = blue ? [1377, 1379, 1434, 1436, 1491, 1493] : [1374, 1376, 1431, 1433, 1488, 1490];
    put(tl, x, y, 'back'); put(tr, x + 1, y, 'back'); put(ml, x, y + 1, 'back'); put(mr, x + 1, y + 1, 'back'); put(bl, x, y + 2, 'back'); put(br, x + 1, y + 2, 'back');
  };
  const potions = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 19, 20, 21, 22];
  const fillShelf = (x: number, y: number, n: number, pool: readonly number[]) => { for (let i = 0; i < n; i++) items.push({ item: pool[Math.floor(r() * pool.length)]!, x: x + i * (0.62 + r() * 0.1), y: y - 0.62 }); };
  /** docs/116 CI16: the band's room upstairs: four beds under quilts along the floor, a wash stand, a
   * candle on a stool, a window onto the night, a trunk, the stairs down on the left. */
  const bedroom = () => {
    windows.push({ x: cx, y: top + 1, h: 2 });
    const beds: InnPlan['beds'] = [];
    for (const bx of [cx - 7, cx - 4, cx + 2, cx + 5]) {
      beds.push({ x: bx, y: F + 1, sleeper: { x: bx + 0.15, y: F + 0.85 } });
      blocks.push({ x: bx, y: F + 1 }, { x: bx + 1, y: F + 1 });
    }
    put(P.chest.closed, cx - 1, F + 1);
    blocks.push({ x: cx + 8, y: F + 1 });                                      // the wash stand (painted by the inn's layer)
    put(P.furniture.cupboard[0], cx + 8, F);
    put(P.furniture.stool, cx - 2, F + 1, 'mid');
    rug = { x: cx - 2, y: F + 3, w: 5, h: 2, blue: true };
    hangs.push({ x: cx - 4, kind: 'lantern' });
    lamps.push({ x: cx - 1.8, y: F + 0.5, kind: 'wall' });
    inn = {
      hearth: [], armchairs: [], fire: { x: cx, y: F }, storyteller: { x: cx, y: F + 1 }, cat: { x: cx + 0.5, y: F + 2.6 }, dog: { x: cx, y: F + 3 },
      notice: { x: 0, y: 0, w: 0, h: 0 }, lectern: { x: 0, y: 0 }, bard: { x: 0, y: 0 },
      bar: { x: 0, w: 0, y: 0, tap: { x: 0, y: 0 }, stand: [] }, kitchen: { x: 0, y: 0 }, bell: { x: 0, y: 0 },
      dice: { x: 0, y: 0, w: 0, stools: [] }, stairs: { x: 1, y: F + 1, top: { x: 1, y: F } },
      candles: [{ x: cx - 1.85, y: F + 0.35, table: -1 }], gaze: [{ x: cx - 0.1, y: F + 1.05 }], beds, wash: { x: cx + 8, y: F + 1 },
    };
  };
  // docs/112: a workshop is furnished as the building its trade is nearest, its wares its own
  const style = STYLE_OF[kind];
  switch (style) {
    case 'inn': {
      if (kind === 'upstairs') { bedroom(); break; }
      // docs/116 CI2: the room laid out across `INN_SPAN` cells: the hearth and its armchairs on the
      // left, the noticeboard and guestbook by them, the long and round tables in the middle, the
      // bard's corner, the bar, the kitchen door, the dice table and the stairs up on the right;
      // a second row of tables in front where the floor is deep enough
      hearth = { x: cx - 9, y: F };
      put(P.fire[0], cx - 8, F - 0.95, 'mid', { anim: P.fire });
      lamps.push({ x: cx - 7.5, y: F + 0.4, kind: 'fire' });
      items.push({ item: 230, x: cx - 7.83, y: F - 3.6 });                       // a shield over the mantel
      const bar = cx + 4;
      for (let i = 0; i < 4; i++) put(P.barrel[i % 2]!, bar + i, F, 'mid');
      for (let i = 0; i < 3; i++) put(P.barrel[(i + 1) % 2]!, bar + i + 0.5, F - 1, 'mid', { dy: 8 });
      shelves.push({ x: bar, y: F - 2, w: 4 }); fillShelf(bar + 0.15, F - 2, 6, [19, 20, 21, 13, 14, 1]);
      put(605, bar, F + 1, 'front'); put(606, bar + 1, F + 1, 'front'); put(606, bar + 2, F + 1, 'front'); put(607, bar + 3, F + 1, 'front');
      keeper = { x: bar + 1.5, y: F - 0.05, flip: true };
      // the long table and the round one, a rug under them
      const t1 = cx - 3;
      rug = { x: t1 - 1, y: F + 2, w: 7, h: 2, blue: false };
      rugs.push({ x: cx - 10, y: F + 1, w: 5, h: 2, blue: false });               // the hearth rug
      table = { x: t1, y: F + 2, w: 3 };
      tables = [table, { x: cx + 1, y: F + 2, w: 2 }];
      put(600, t1, F + 2, 'front'); put(601, t1 + 1, F + 2, 'front'); put(602, t1 + 2, F + 2, 'front');
      put(603, cx + 1, F + 2, 'front'); put(604, cx + 2, F + 2, 'front');
      for (const sx of [t1, t1 + 2, cx + 1, cx + 2]) put(P.furniture.stool, sx, F + 3, 'front', { dy: -3 });
      // a second row of tables in front, where the floor runs deep enough to walk round them
      if (floorRows >= 6) {
        const front = [{ x: cx - 6, w: 2 }, { x: cx + 2, w: 3 }];
        for (const ft of front) {
          const ty = F + 4;
          tables.push({ x: ft.x, y: ty, w: ft.w });
          if (ft.w === 2) { put(603, ft.x, ty, 'front'); put(604, ft.x + 1, ty, 'front'); }
          else { put(600, ft.x, ty, 'front'); put(601, ft.x + 1, ty, 'front'); put(602, ft.x + 2, ty, 'front'); }
          for (let i = 0; i < ft.w; i += 2) put(P.furniture.stool, ft.x + i, ty + 1, 'front', { dy: -3 });
          lamps.push({ x: ft.x + ft.w / 2, y: ty - 0.2, kind: 'wall' });
        }
      }
      for (const sx of [t1 + 1, cx + 1.5, t1, cx + 2.4, t1 + 2]) others.push({ x: sx, y: F + 1.25, seated: true });
      others.push({ x: bar - 0.9, y: F + 1.05, flip: true }, { x: cx - 0.55, y: F + 1.1 });
      // the walls: the noticeboard where the banner hung, crests over the bar, rafters hung with lanterns
      windows.push({ x: cx - 2, y: top + 1, h: 2 }, { x: cx + 2, y: top + 1, h: 2 });
      put(P.heraldry[Math.floor(r() * 3)]!, cx + 4, top + 1, 'back');
      put(P.heraldry[(1 + Math.floor(r() * 2)) % 3]!, cx + 7, top + 1, 'back');
      hangs.push({ x: cx - 1.5, kind: 'chandelier' }, { x: cx + 5, kind: 'lantern' }, { x: cx - 7.5, kind: 'lantern' }, { x: cx + 9.5, kind: 'lantern' });
      lamps.push({ x: cx - 1, y: top + 0.6, kind: 'wall' }, { x: cx + 5.5, y: top + 0.4, kind: 'wall' }, { x: cx - 7, y: top + 0.4, kind: 'wall' }, { x: cx + 10, y: top + 0.4, kind: 'wall' }, { x: t1 + 1.5, y: F + 1.8, kind: 'wall' }, { x: cx + 2, y: F + 1.8, kind: 'wall' });
      // the dice table and its four stools (two behind, one at each end), near the bar
      const dx = cx + 9, dy = F + 3;
      const stools: Station[] = [{ x: dx - 1.05, y: dy - 0.15, seated: true }, { x: dx - 0.05, y: dy - 0.75, seated: true }, { x: dx + 0.95, y: dy - 0.75, seated: true, flip: true }, { x: dx + 2.0, y: dy - 0.15, seated: true, flip: true }];
      blocks.push({ x: dx, y: dy, front: true }, { x: dx + 1, y: dy, front: true });
      const candles = (tables ?? []).map((t, i) => ({ x: t.x + t.w / 2 - 0.15, y: t.y + 0.05, table: i }));
      // the armchairs and the little table by them are the inn layer's paint, and solid (the lectern stands against the wall)
      blocks.push({ x: cx - 10.6, y: F + 2.25 }, { x: cx - 6.4, y: F + 2.25 }, { x: cx - 11.7, y: F + 1.9 });
      // a gallery across the upper back wall when the rafters run three rows or more: the stairs climb to it
      const gallery = top >= 3 ? { y: top - 1 / 3, x0: cx - 6.2, x1: w, doors: [cx - 3, cx + 2.5, cx + 7.5] } : undefined;
      inn = {
        hearth: [{ x: cx - 9.4, y: F + 1.15 }, { x: cx - 8.4, y: F + 1.3, flip: true }, { x: cx - 7.6, y: F + 1.15, flip: true }, { x: cx - 10.2, y: F + 1.35 }, { x: cx - 6.8, y: F + 1.4, flip: true }, { x: cx - 8.9, y: F + 1.7 }],
        armchairs: [{ x: cx - 10.6, y: F + 2.25, seated: true }, { x: cx - 6.4, y: F + 2.25, seated: true, flip: true }],
        fire: { x: cx - 7.5, y: F + 0.2 },
        storyteller: { x: cx - 5.4, y: F + 1.05, seated: true, flip: true },
        cat: { x: cx - 8.4, y: F + 1.85 },
        dog: { x: 1.75, y: F + 1.3 },
        notice: { x: cx - 5.4, y: top + 1.15, w: 2, h: 2 },
        lectern: { x: cx - 4.1, y: F + 0.95 },
        bard: { x: cx + 3.05, y: F + 0.95, seated: true, flip: true },
        bar: { x: bar, w: 4, y: F + 1, tap: { x: bar + 3.4, y: F + 0.2 }, stand: [{ x: bar + 0.3, y: F + 1.75 }, { x: bar + 1.2, y: F + 1.85 }, { x: bar + 2.1, y: F + 1.75 }, { x: bar + 3, y: F + 1.85 }] },
        kitchen: { x: cx + 8.2, y: F + 0.1, flip: true }, bell: { x: cx + 8.95, y: F - 1.4 },
        dice: { x: dx, y: dy, w: 2, stools },
        stairs: { x: cx + 11, y: F + 1, top: gallery ? { x: cx + 12.6, y: gallery.y - 1 } : { x: cx + 12, y: top + 0.5 } },
        candles,
        gaze: [{ x: cx - 2.1, y: F + 1.05 }, { x: cx + 1.9, y: F + 1.05 }],
        beds: [],
        side: { x: cx - 11.7, y: F + 1.9 },
        ...(gallery ? { gallery } : {}),
      };
      break;
    }
    case 'shop': {
      // shelves of goods along the back wall, a counter in the middle, chests and urns on the floor
      for (let i = -7; i <= 7; i++) {
        if (i >= -1 && i <= 1) continue;
        put(i % 2 ? 543 : 545, cx + i, F, 'mid');
      }
      put(P.furniture.bookshelf, cx - 1, F); put(P.furniture.cupboard[0], cx, F); put(P.furniture.cupboard[1], cx + 1, F);
      // wall shelves of potions and curios; arms and armour hung for sale
      shelves.push({ x: cx - 7, y: F - 1, w: 5 }, { x: cx + 3, y: F - 1, w: 5 }, { x: cx - 7, y: F - 2.6, w: 5 }, { x: cx + 3, y: F - 2.6, w: 5 });
      fillShelf(cx - 6.8, F - 1, 7, potions); fillShelf(cx + 3.2, F - 1, 7, potions); fillShelf(cx - 6.8, F - 2.6, 7, [23, 24, 25, 26, 59, 60, 61, 62, 72, 73]); fillShelf(cx + 3.2, F - 2.6, 7, [136, 137, 67, 68, 69, 70, 71, 145]);
      for (const [it, x] of [[199, -1.6], [191, -0.8], [221, 0], [229, 0.8], [196, 1.6]] as const) items.push({ item: it, x: cx + x - 0.3, y: top + 1.6 + (Math.abs(x) > 1 ? 0.15 : 0) });
      put(600, cx - 1, F + 1, 'front'); put(601, cx, F + 1, 'front'); put(602, cx + 1, F + 1, 'front');
      items.push({ item: 72, x: cx - 0.7, y: F + 0.75 }, { item: 9, x: cx + 0.6, y: F + 0.72 });
      keeper = { x: cx, y: F + 0.3 };
      put(P.chest.closed, cx - 5, F + 1); put(P.chest.gold, cx + 4, F + 2); put(325, cx - 3, F + 3); put(326, cx + 5, F + 3);
      put(P.urns.blue[0], cx - 7, F + 1); put(P.urns.green[0], cx - 6, F + 1); put(P.urns.red[1], cx + 6, F + 2); put(P.urns.orange[0], cx + 7, F + 2);
      put(P.crate[0], cx - 8, F); put(P.crate[1], cx + 8, F); put(P.barrel[0], cx + 8, F + 1);
      rug = { x: cx - 2, y: F + 2, w: 5, h: 2, blue: false };
      others.push({ x: cx - 3, y: F + 1.1 }, { x: cx + 3, y: F + 1.1, flip: true }, { x: cx - 7, y: F + 1.2 });
      hangs.push({ x: cx - 4, kind: 'lantern' }, { x: cx + 4, kind: 'lantern' });
      lamps.push({ x: cx - 3.5, y: top + 0.6, kind: 'wall' }, { x: cx + 4.5, y: top + 0.6, kind: 'wall' }, { x: cx + 0.5, y: F + 1.2, kind: 'wall' });
      break;
    }
    case 'smithy': {
      // the forge on the right (a fire under a hood), the anvil before it, arms on racks and on the wall
      hearth = { x: cx + 2, y: F };
      put(P.fire[0], cx + 3, F - 0.95, 'mid', { anim: P.fire });
      lamps.push({ x: cx + 3.5, y: F + 0.4, kind: 'fire' });
      put(P.brazier[0], cx + 7, F + 1, 'mid', { anim: P.brazier });
      lamps.push({ x: cx + 7.5, y: F + 1.2, kind: 'fire' });
      keeper = { x: cx + 1, y: F + 1.05, flip: true };
      put(P.furniture.tableCandles[0], cx - 6, F); put(P.furniture.tableCandles[1], cx - 5, F); put(P.furniture.tableCandles[0], cx - 4, F);
      put(P.barrel[1], cx + 6, F + 2); put(P.bucket[0], cx + 5, F + 3);
      put(P.crate[0], cx - 8, F); put(P.crate[1], cx - 8, F + 1); put(P.greyPile, cx - 7, F + 1);
      put(P.chest.closed, cx - 2, F);
      // tools and blades hung on the wall
      shelves.push({ x: cx - 7, y: F - 1.2, w: 5 });
      const arms = [164, 165, 166, 167, 168, 169, 199, 200, 201, 202, 203, 211, 212, 213, 221, 222, 223];
      for (let i = 0; i < 8; i++) items.push({ item: arms[Math.floor(r() * arms.length)]!, x: cx - 7 + i * 0.66, y: F - 2.6 + (i % 2) * 0.15 });
      fillShelf(cx - 6.8, F - 1.2, 7, [175, 176, 161, 162, 152, 153, 154, 155]);
      for (let i = 0; i < 3; i++) items.push({ item: [221, 222, 224][i]!, x: cx + 6.4 + i * 0.75, y: top + 1.6 });
      for (let i = 0; i < 4; i++) items.push({ item: [175, 164, 176, 165][i]!, x: cx + 6.2 + i * 0.6, y: top + 2.6 });
      others.push({ x: cx - 5, y: F + 1.1 }, { x: cx - 2.4, y: F + 1.2 });
      windows.push({ x: cx - 2, y: top + 1, h: 2 });
      hangs.push({ x: cx - 4, kind: 'lantern' });
      lamps.push({ x: cx - 3.5, y: top + 0.6, kind: 'wall' });
      break;
    }
    case 'temple': {
      // the altar under a tall window, statues either side, braziers, blue banners, pews
      put(P.pedestals.altar, cx, F, 'mid');
      put(P.statues.hooded, cx - 3, F); put(P.statues.knight, cx + 3, F);
      put(P.statues.archer, cx - 7, F); put(P.statues.hooded, cx + 7, F);
      put(P.brazier[0], cx - 1, F, 'mid', { anim: P.brazier }); put(P.brazier[1], cx + 1, F, 'mid', { anim: [P.brazier[1], P.brazier[0]] });
      lamps.push({ x: cx - 0.5, y: F + 0.4, kind: 'fire' }, { x: cx + 1.5, y: F + 0.4, kind: 'fire' });
      banner(cx - 5, top + 1, true); banner(cx + 4, top + 1, true);
      put(1548, cx, top + 1.2, 'back');
      items.push({ item: 145, x: cx - 0.6, y: F - 0.55 }, { item: 145, x: cx + 0.6, y: F - 0.55 });
      for (const x of [cx - 7, cx + 2]) pews.push({ x, y: F + 2.6, w: 5 });
      for (const x of [cx - 7, cx + 2]) pews.push({ x, y: F + 3.7, w: 5 });
      rug = { x: cx - 1, y: F + 1, w: 3, h: ROOM_FLOOR - 1, blue: true };
      keeper = { x: cx, y: F + 0.9 };
      others.push({ x: cx - 4.5, y: F + 1.9, seated: true }, { x: cx + 4.5, y: F + 1.9, seated: true }, { x: cx - 8, y: F + 1.1 });
      windows.push({ x: cx - 8, y: top + 0.5, h: 3 }, { x: cx + 7, y: top + 0.5, h: 3 }, { x: cx - 2, y: top + 0.5, h: 2 }, { x: cx + 2, y: top + 0.5, h: 2 });
      break;
    }
  }
  // the enchanter's and stargazer's towers: a study, not a chapel — shelves of their wares where the
  // pews and the holy sign would be
  if (style === 'temple' && kind !== 'temple') {
    pews.length = 0;
    // the holy sign and the banners come down; shelves go up where the banners hung
    const holy = new Set([1548, 1374, 1375, 1376, 1377, 1378, 1379, 1431, 1432, 1433, 1434, 1435, 1436, 1488, 1489, 1490, 1491, 1492, 1493]);
    for (let i = props.length - 1; i >= 0; i--) if (holy.has(props[i]!.frame)) props.splice(i, 1);
    for (const x of [cx - 5.6, cx + 3.4]) {
      for (const y of [top + 2.2, top + 3.8]) { shelves.push({ x, y, w: 3 }); fillShelf(x + 0.15, y, 4, [0]); }
    }
    put(P.furniture.bookshelf, cx - 5, F); put(P.furniture.bookshelf, cx + 5, F);
    put(P.chest.closed, cx + 7, F + 2); put(P.urns.blue[0], cx + 8, F + 2);
    rug = { x: cx - 3, y: F + 2, w: 7, h: 2, blue: true };
  }
  // docs/142: the dyer's yard is the tailor's shop cleared of its wares: vats of dye along the back, lengths of dyed
  // cloth hung on lines across the wall above them, bolts and crates by the counter
  let dyer: Room['dyer'];
  if (kind === 'dyer') {
    shelves.length = 0; items.length = 0; others.length = 0;
    const keep = new Set([600, 601, 602]);
    for (let i = props.length - 1; i >= 0; i--) if (!keep.has(props[i]!.frame)) props.splice(i, 1);
    put(P.barrel[0], cx - 9, F + 1); put(P.barrel[1], cx + 8, F + 1); put(P.bucket[0], cx - 2, F + 3); put(P.bucket[1], cx + 7.5, F + 2);
    put(P.urns.blue[0], cx - 9, F); put(P.urns.red[0], cx + 8, F); put(P.urns.green[1], cx + 9, F);
    const pick = (i: number) => 1 + Math.floor(hashN(seed, i, 43) * 12);
    // the vats hold the boldest six (blue, red, gold, green, violet, teal), in a seeded order
    const bold = [9, 8, 11, 10, 12, 7], turn = Math.floor(hashN(seed, 7, 43) * bold.length);
    const vats = [cx - 7, cx - 5.5, cx - 4, cx + 3, cx + 4.5, cx + 6].map((x, i) => ({ x, y: F + 1, dye: bold[(i + turn) % bold.length]! }));
    for (const v of vats) blocks.push({ x: v.x, y: v.y });
    dyer = { vats, lines: [{ x: cx - 8, y: top + 0.6, w: 6, from: pick(9) }, { x: cx + 2, y: top + 0.6, w: 6, from: pick(10) }, { x: cx - 2, y: top + 1.6, w: 4, from: pick(11) }] };
    others.push({ x: cx - 3, y: F + 2.1 }, { x: cx + 2.6, y: F + 2.2, flip: true });
  }
  const wares = WORKSHOP_WARES[kind as WorkshopKind];
  if (wares) for (let i = 0; i < items.length; i++) items[i] = { ...items[i]!, item: wares[Math.floor(hashN(seed, i, 41) * wares.length)]! };
  // docs/116: in the inn the band comes in in front of the armchairs by the hearth
  // docs/116: in the inn the band comes in in front of the armchairs by the hearth
  const band: Station = { x: 2.3, y: style === 'temple' ? F + 1.45 : kind === 'inn' ? Math.min(F + 3.25, h - 1.6) : F + 2.6 };
  return { kind, w, h, floor: F, top, props, lamps, keeper, others, exit: 1, band, windows, shelves, items, hangs, pews, ...(rug ? { rug } : {}), ...(hearth ? { hearth } : {}), ...(table ? { table } : {}), ...(tables ? { tables } : {}),
    ...(rugs.length ? { rugs } : {}), ...(blocks.length ? { blocks } : {}), ...(inn ? { inn } : {}), ...(dyer ? { dyer } : {}) };
}

/** docs/112: what hangs on a workshop's walls and stands on its shelves (item-atlas icons). */
const WORKSHOP_WARES: Partial<Record<WorkshopKind, readonly number[]>> = {
  enchanter: [89, 90, 91, 92, 93, 50, 51, 52, 54, 70, 154],
  stargazer: [93, 89, 92, 67, 69, 70, 136, 101],
  hedgewitch: [94, 95, 7, 9, 12, 14, 19, 133, 135, 71],
  runecarver: [234, 226, 164, 166, 69, 136],
  weaponsmith: [203, 206, 209, 210, 211, 213, 214, 215, 217, 164],
  armourer: [221, 222, 223, 224, 226, 228, 229, 232, 150, 260],
  bowyer: [191, 192, 193, 194, 196, 197],
  leatherworker: [235, 258, 260, 265, 275],
};

/** Is this spot inside a building (the view lays a room) rather than the street? */
export const isInside = (s: VillageSpot): s is BuildingKind => s !== 'street';

/** The keeper of each building is found by their epithet; otherwise the first villager there. */
export const KEEPER_WORDS: Partial<Record<BuildingKind, RegExp>> = { inn: /inn|tavern|host|barkeep|alewife/i, shop: /merchant|shop|trader|grocer|chandler/i, smithy: /smith|forge|armou?rer/i, temple: /priest|priestess|cleric|abbot|acolyte|temple|monk|nun/i };
